import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  appendPropertyTableDraftRow,
  removePropertyTableDraftRowById,
} from 'redux/BuilderModeSlice';
import {
  getSelectedCell,
  handleSelectedCell,
  UpdateIndicator,
  AddIndicator,
  ReorderIndicator,
  DeleteIndicator,
  handleAddIndicator,
  getAddIndicator,
} from 'redux/PlannerModeSlice';

import { reorderArray } from '../TableHelpersOpz';

import { PROPERTY_TABLE_COLUMNS, isBuilderPropertyDraftRowId } from './constants';
import {
  useStablePropertyCellTemplates,
  type PropertyCellTemplateCallbacks,
} from './useStablePropertyCellTemplates';

type Arguments = {
  blockId: string | undefined;
  refetchMetadata: () => void;
  tableData: any[];
  rows: any;
  planPermissionReadOnly: boolean;
};

export default function usePropertyTableInteractionController({
  blockId,
  refetchMetadata,
  tableData,
  rows,
  planPermissionReadOnly,
}: Arguments) {
  const dispatch = useDispatch<any>();
  const selectedCell = useSelector(getSelectedCell);
  const addIndicator = useSelector(getAddIndicator);

  const [gridProperties, setGridProperties] = useState<Record<string, unknown>>({});
  const gridPropertiesReference = useRef(gridProperties);
  gridPropertiesReference.current = gridProperties;
  const [deleteModal, setDeleteModal] = useState({
    indicatorId: '',
    indicatorName: '',
    status: false,
  });
  const [savingPropertyCell, setSavingPropertyCell] = useState<{
    rowId: string | number;
    columnId: string;
  } | null>(null);

  const tableDataReference = useRef(tableData);
  const rowsReference = useRef(rows);
  const selectedCellReference = useRef(selectedCell);
  tableDataReference.current = tableData;
  rowsReference.current = rows;
  selectedCellReference.current = selectedCell;

  const pendingDeleteReference = useRef({ id: '', name: '' });
  const draftRowIdReference = useRef(0);

  const handleDeleteRowPrompt = useCallback(
    (data: any) => {
      if (isBuilderPropertyDraftRowId(data?.id)) {
        dispatch(removePropertyTableDraftRowById(data.id));
        return;
      }
      pendingDeleteReference.current = { id: data?.id, name: data?.itemName ?? '' };
      setDeleteModal({
        indicatorId: data?.id,
        indicatorName: data?.itemName,
        status: true,
      });
    },
    [dispatch],
  );

  const handleFocusLocationChanged = useCallback(
    (data: any) => {
      if (selectedCellReference.current?.rowId !== data?.rowId) dispatch(handleSelectedCell(data));
    },
    [dispatch],
  );

  const handleAddRow = useCallback(() => {
    draftRowIdReference.current += 1;
    const draftRowId = `builder-property-draft-${draftRowIdReference.current}`;
    const columnDefaults = Object.fromEntries(
      PROPERTY_TABLE_COLUMNS.map((col) => [col.name, ''] as [string, string]),
    );

    const object: any = {
      ...columnDefaults,
      name: '',
      id: draftRowId,
      position: '',
      output: false,
      'Display as output': false,
      isNewCell: true,
    };
    dispatch(appendPropertyTableDraftRow(object));
    setGridProperties({
      focusLocation: { rowId: draftRowId, columnId: '0' },
    });
    dispatch(handleAddIndicator(false));
  }, [dispatch]);

  useEffect(() => {
    if (addIndicator) handleAddRow();
  }, [addIndicator, handleAddRow]);

  const updateIndicator = useCallback(
    (changes: any, change: any) => {
      const first = changes[0];
      const rowId = first?.rowId;
      const columnId = first?.columnId != null ? String(first.columnId) : null;
      if (rowId != null && columnId != null) {
        setSavingPropertyCell({ rowId, columnId });
      }
      dispatch(
        UpdateIndicator({
          indicatorId: changes[0]?.rowId,
          data: {
            [change?.key]: change?.extraKey
              ? change?.checked
                ? 'subtotal'
                : 'default'
              : change?.checked !== undefined
              ? `${change?.checked}`
              : change?.text,
          },
        }),
      ).finally(() => {
        setSavingPropertyCell(null);
        dispatch(handleSelectedCell(selectedCellReference.current));
      });
    },
    [dispatch],
  );

  const handleAddDimIndicator = useCallback(
    (indicatorName: string, committedDraftRowId: number | string) => {
      dispatch(
        AddIndicator({
          blockId,
          data: {
            name: indicatorName,
            type: 'Input',
          },
          toaster: true,
          committedDraftRowId,
        }),
      ).then((response: any) => {
        if (!response?.error) {
          setGridProperties({
            focusLocation: { rowId: 'header', columnId: '0' },
          });
        }
      });
    },
    [blockId, dispatch],
  );

  const handleCellChange = useCallback(
    (changes: any) => {
      const change = changes[0];
      if (change?.newCell?.isNewCell) {
        handleAddDimIndicator(change?.newCell?.text, change?.rowId);
      } else {
        updateIndicator(changes, change?.newCell);
      }
    },
    [handleAddDimIndicator, updateIndicator],
  );

  const handleRowsReorder = useCallback(
    async (targetRowId: any, rowIds: any[]) => {
      const td = tableDataReference.current;
      const to = td?.findIndex((person: any) => person.id === targetRowId);
      const rowsIds = rowIds.map((id) => td?.findIndex((person: any) => person.id === id));
      const reorderedArray = reorderArray(td, rowsIds, to);
      dispatch(
        ReorderIndicator({
          blockId,
          data: { indicators: reorderedArray },
        }),
      ).then((response: any) => {
        if (!response?.error) refetchMetadata();
      });
    },
    [blockId, dispatch, refetchMetadata],
  );

  const handleDeleteConfirm = useCallback(() => {
    const indicatorId = pendingDeleteReference.current.id;
    setDeleteModal({ indicatorId: '', indicatorName: '', status: false });
    dispatch(
      DeleteIndicator({
        indicatorId,
        toaster: {
          successMessage: 'Indicator deleted',
        },
      }),
    ).then((response: any) => {
      if (!response?.error) {
        setGridProperties({
          focusLocation: { rowId: 'header', columnId: '0' },
        });
        refetchMetadata();
      }
    });
  }, [dispatch, refetchMetadata]);

  const handleContextMenu = useCallback(
    (
      _selectedRowIds: any,
      _selectedColIds: any,
      _selectionMode: any,
      menuOptions: any,
      selectedRanges: any,
    ) => {
      if (
        selectedRanges[0] &&
        selectedRanges[0][0]?.rowId !== 'header' &&
        selectedRanges[0][0]?.rowId !== 'footer'
      ) {
        const gridRows = rowsReference.current;
        const deleteRow = {
          id: 'deleteRow',
          label: 'Delete row',
          handler: () => {
            const myRow = gridRows?.filter((data2: any) => {
              return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
            });
            handleDeleteRowPrompt({ id: myRow[0]?.itemId, itemName: myRow[0]?.itemName });
          },
        };
        const range = selectedRanges[0];
        const selectedRows =
          Boolean(range?.length) && range[0]?.rowId === range[range.length - 1]?.rowId;
        if (selectedRanges[0][0]?.columnId !== 'delete' && selectedRows) {
          const filtered = menuOptions.filter((object: any) => object.id !== 'cut');
          return [...filtered, deleteRow];
        }
        return selectedRanges[0][0]?.columnId === 'delete' && selectedRanges[0]?.length === 1
          ? [deleteRow]
          : menuOptions.filter((object: any) => object.id !== 'cut');
      }
      return [];
    },
    [handleDeleteRowPrompt],
  );

  const handleLocationChanged = useCallback(() => {
    if (Object.keys(gridPropertiesReference.current).length > 0) {
      setGridProperties({});
    }
    return true;
  }, []);

  const cellCallbacksReference = useRef<PropertyCellTemplateCallbacks>({
    onAddRow: () => {},
    onDeleteRowPrompt: () => {},
  });
  cellCallbacksReference.current.onAddRow = handleAddRow;
  cellCallbacksReference.current.onDeleteRowPrompt = handleDeleteRowPrompt;

  const customCellTemplates = useStablePropertyCellTemplates(
    planPermissionReadOnly,
    cellCallbacksReference,
  );

  return {
    gridProperties,
    deleteModal,
    setDeleteModal,
    customCellTemplates,
    savingPropertyCell,
    handleFocusLocationChanged,
    handleCellChange,
    handleRowsReorder,
    handleContextMenu,
    handleLocationChanged,
    handleDeleteConfirm,
  };
}
